<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatTime } from '@chatwoot/utils';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import ReportHeader from './components/ReportHeader.vue';
import OverviewReportFilters from './components/OverviewReportFilters.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components/table/Table.vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { h } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const from = ref(getUnixStartOfDay(subDays(new Date(), 6)));
const to = ref(getUnixEndOfDay(new Date()));
const businessHours = ref(false);
const currentPage = ref(1);
const selectedAgentId = ref('');

const uiFlags = useMapGetter('analyticsReports/getUIFlags');
const agentMetrics = useMapGetter('analyticsReports/getAgentMetrics');
const waitingConversations = useMapGetter(
  'analyticsReports/getWaitingConversations'
);
const summary = useMapGetter('analyticsReports/getSummary');
const waitingTotal = useMapGetter('analyticsReports/getWaitingTotal');
const waitingTotalPages = useMapGetter(
  'analyticsReports/getWaitingTotalPages'
);
const agents = useMapGetter('agents/getAgents');

const isLoading = computed(() => uiFlags.value.isFetching);

const fetchData = () => {
  store.dispatch('analyticsReports/fetchAnalytics', {
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
    page: currentPage.value,
    assigneeId: selectedAgentId.value || undefined,
  });
};

const onFilterChange = filter => {
  from.value = filter.from;
  to.value = filter.to;
  businessHours.value = filter.businessHours;
  currentPage.value = 1;
  fetchData();
};

const onAgentFilter = event => {
  selectedAgentId.value = event.target.value;
  currentPage.value = 1;
  fetchData();
};

const onPageChange = page => {
  currentPage.value = page;
  fetchData();
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
});

const renderTime = value => (value ? formatTime(value) : '--');
const renderCount = value =>
  value !== null && value !== undefined ? value.toLocaleString() : '--';

const accountId = computed(() => route.params.accountId);

const summaryCards = computed(() => [
  {
    label: t('ANALYTICS_REPORTS.CARDS.TOTAL_CONVERSATIONS'),
    value: renderCount(summary.value.total_conversations),
    icon: 'i-lucide-message-square',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.OPEN_CONVERSATIONS'),
    value: renderCount(summary.value.open_conversations),
    icon: 'i-lucide-message-circle',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.RESOLVED_CONVERSATIONS'),
    value: renderCount(summary.value.resolved_conversations),
    icon: 'i-lucide-check-circle',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.PENDING_CONVERSATIONS'),
    value: renderCount(summary.value.pending_conversations),
    icon: 'i-lucide-clock',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.AVG_FIRST_RESPONSE'),
    value: renderTime(summary.value.avg_first_response_time),
    icon: 'i-lucide-timer',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.AVG_RESOLUTION_TIME'),
    value: renderTime(summary.value.avg_resolution_time),
    icon: 'i-lucide-hourglass',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.INCOMING_MESSAGES'),
    value: renderCount(summary.value.incoming_messages),
    icon: 'i-lucide-arrow-down-left',
  },
  {
    label: t('ANALYTICS_REPORTS.CARDS.OUTGOING_MESSAGES'),
    value: renderCount(summary.value.outgoing_messages),
    icon: 'i-lucide-arrow-up-right',
  },
]);

// Agent metrics table
const agentColumnHelper = createColumnHelper();

const agentColumns = computed(() => [
  agentColumnHelper.accessor('name', {
    header: t('ANALYTICS_REPORTS.TABLE.AGENT'),
    width: 200,
  }),
  agentColumnHelper.accessor('conversations_count', {
    header: t('ANALYTICS_REPORTS.TABLE.CONVERSATIONS'),
    width: 120,
    cell: props => h('span', {}, renderCount(props.getValue())),
  }),
  agentColumnHelper.accessor('avg_first_response_time', {
    header: t('ANALYTICS_REPORTS.TABLE.AVG_FRT'),
    width: 150,
    cell: props => h('span', {}, renderTime(props.getValue())),
  }),
  agentColumnHelper.accessor('min_first_response_time', {
    header: t('ANALYTICS_REPORTS.TABLE.MIN_FRT'),
    width: 130,
    cell: props => h('span', {}, renderTime(props.getValue())),
  }),
  agentColumnHelper.accessor('max_first_response_time', {
    header: t('ANALYTICS_REPORTS.TABLE.MAX_FRT'),
    width: 130,
    cell: props => h('span', {}, renderTime(props.getValue())),
  }),
  agentColumnHelper.accessor('resolved_count', {
    header: t('ANALYTICS_REPORTS.TABLE.RESOLVED'),
    width: 120,
    cell: props => h('span', {}, renderCount(props.getValue())),
  }),
  agentColumnHelper.accessor('avg_resolution_time', {
    header: t('ANALYTICS_REPORTS.TABLE.AVG_RESOLUTION'),
    width: 150,
    cell: props => h('span', {}, renderTime(props.getValue())),
  }),
  agentColumnHelper.accessor('avg_reply_time', {
    header: t('ANALYTICS_REPORTS.TABLE.AVG_REPLY'),
    width: 150,
    cell: props => h('span', {}, renderTime(props.getValue())),
  }),
]);

const agentTable = useVueTable({
  get data() {
    return agentMetrics.value || [];
  },
  get columns() {
    return agentColumns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// Waiting conversations table
const waitingColumnHelper = createColumnHelper();

const formatWaitingTime = seconds => {
  if (!seconds) return '--';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const waitingColumns = computed(() => [
  waitingColumnHelper.accessor('id', {
    header: '#',
    width: 70,
    cell: props =>
      h(
        'a',
        {
          href: frontendURL(
            `accounts/${accountId.value}/conversations/${props.getValue()}`
          ),
          class: 'text-n-brand hover:underline font-medium',
          target: '_blank',
        },
        `#${props.getValue()}`
      ),
  }),
  waitingColumnHelper.accessor('contact_name', {
    header: t('ANALYTICS_REPORTS.WAITING.CONTACT'),
    width: 160,
  }),
  waitingColumnHelper.accessor('assignee_name', {
    header: t('ANALYTICS_REPORTS.WAITING.ASSIGNEE'),
    width: 150,
    cell: props =>
      h(
        'span',
        { class: props.getValue() ? '' : 'text-n-slate-10 italic' },
        props.getValue() || t('ANALYTICS_REPORTS.WAITING.UNASSIGNED')
      ),
  }),
  waitingColumnHelper.accessor('inbox_name', {
    header: t('ANALYTICS_REPORTS.WAITING.INBOX'),
    width: 150,
  }),
  waitingColumnHelper.accessor('waiting_seconds', {
    header: t('ANALYTICS_REPORTS.WAITING.WAITING_TIME'),
    width: 130,
    cell: props => {
      const secs = props.getValue();
      const cls = secs > 3600 ? 'text-n-ruby-11 font-semibold' : '';
      return h('span', { class: cls }, formatWaitingTime(secs));
    },
  }),
  waitingColumnHelper.accessor('last_message_content', {
    header: t('ANALYTICS_REPORTS.WAITING.LAST_MESSAGE'),
    width: 250,
    cell: props =>
      h(
        'span',
        { class: 'text-n-slate-11 truncate block max-w-[250px]' },
        props.getValue() || '--'
      ),
  }),
]);

const waitingTable = useVueTable({
  get data() {
    return waitingConversations.value || [];
  },
  get columns() {
    return waitingColumns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const waitingCount = computed(() => waitingTotal.value || 0);
const totalPages = computed(() => waitingTotalPages.value || 1);
const hasPrevPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(() => currentPage.value < totalPages.value);
</script>

<template>
  <ReportHeader
    :header-title="t('ANALYTICS_REPORTS.HEADER')"
    :header-description="t('ANALYTICS_REPORTS.DESCRIPTION')"
  />

  <OverviewReportFilters :disabled="isLoading" @filter-change="onFilterChange" />

  <!-- Loading overlay -->
  <div v-if="isLoading" class="flex items-center justify-center py-20">
    <Spinner :size="32" class="text-n-brand" />
  </div>

  <template v-else>
    <!-- Summary Cards -->
    <section class="mt-6">
      <h3 class="text-heading-3 text-n-slate-12 mb-4">
        {{ t('ANALYTICS_REPORTS.SUMMARY_TITLE') }}
      </h3>
      <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <div class="flex items-center gap-2 text-n-slate-11">
            <span :class="card.icon" class="size-4" />
            <span class="text-sm">{{ card.label }}</span>
          </div>
          <span class="text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </span>
        </div>
      </div>
    </section>

    <!-- Agent Metrics Table -->
    <section class="mt-8">
      <h3 class="text-heading-3 text-n-slate-12 mb-4">
        {{ t('ANALYTICS_REPORTS.AGENT_TITLE') }}
      </h3>
      <div
        class="relative flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <Table :table="agentTable" />
      </div>
    </section>

    <!-- Waiting Conversations Table -->
    <section class="mt-8 pb-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('ANALYTICS_REPORTS.WAITING_TITLE', { count: waitingCount }) }}
        </h3>
        <select
          :value="selectedAgentId"
          class="h-8 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          @change="onAgentFilter"
        >
          <option value="">
            {{ t('ANALYTICS_REPORTS.FILTER.ALL_AGENTS') }}
          </option>
          <option
            v-for="agent in agents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
      </div>
      <div
        v-if="waitingCount > 0"
        class="relative flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <Table :table="waitingTable" />
      </div>
      <div
        v-else
        class="flex items-center justify-center rounded-xl border border-n-weak bg-n-solid-2 py-12 text-n-slate-11"
      >
        {{ t('ANALYTICS_REPORTS.NO_WAITING') }}
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between mt-4"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('ANALYTICS_REPORTS.PAGINATION.INFO', { page: currentPage, total: totalPages }) }}
        </span>
        <div class="flex items-center gap-2">
          <V4Button
            :label="t('ANALYTICS_REPORTS.PAGINATION.PREV')"
            icon="i-lucide-chevron-left"
            size="sm"
            variant="hollow"
            :disabled="!hasPrevPage"
            @click="onPageChange(currentPage - 1)"
          />
          <V4Button
            :label="t('ANALYTICS_REPORTS.PAGINATION.NEXT')"
            icon="i-lucide-chevron-right"
            size="sm"
            variant="hollow"
            :disabled="!hasNextPage"
            @click="onPageChange(currentPage + 1)"
          />
        </div>
      </div>
    </section>
  </template>
</template>
